import { FC, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useParams } from "react-router-dom"
import { useForm, useFieldArray, Controller } from "react-hook-form"
import Input from "UI/Input"
import { useUpdateLoanApplication } from "Hooks/API/useLoansApplications"
import { LoanPurposeType } from "Interfaces/loanApplicationInterfaces"
import Button from "UI/Button"
import Select from "UI/Select"
import Radio from "UI/Radio/Radio"
import styled from "styled-components"
import { useFeedbackModal } from "Context/modal-context"
import IconFactory from "UI/IconFactory"
import { useOptions } from "./purposes"
import { LoanPerksBox } from "../../Shared"
import { StepPropsType } from "../../Onboarding.types"
import * as Styles from "../../Onboarding.styles"
import { ExpenseInfoModal } from "./ExpenseInfoModal"

const StyledCloseButton = styled.button`
  color: var(--brand-danger);
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  margin-top: 0px !important;
  justify-content: flex-start;
  white-space: nowrap;
`

const InfoIconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: var(--dark-gray);

  &:hover {
    opacity: 0.8;
  }
`

const EXPENSES_TYPES = {
  CSN_LOAN: "csn_loan",
  PRIVATE_DEBT: "private_debt",
  OTHER_DEBT: "other_debt",
  PRIVATE_LEASING: "private_leasing",
  GUARANTEE_COMMITMENT: "guarantee_commitment",
}

type ExpenseType =
  | "csn_loan"
  | "private_debt"
  | "other_debt"
  | "private_leasing"
  | "guarantee_commitment"

const expenses = {
  [EXPENSES_TYPES.CSN_LOAN]: {
    label: "Studielån från CSN",
    description: "Ange totalt månadsbelopp för lån du har från CSN.",
    placeholder: "Återbetalning, studielån från CSN (kr/mån)",
    monthly_cost: true,
    additional_info: false,
  },
  [EXPENSES_TYPES.PRIVATE_DEBT]: {
    label: "Lån från privatpersoner",
    description:
      "Ange totalt månadsbelopp för lån du har från andra privatpersoner.",
    placeholder: "Återbetalning av privata lån (kr/mån)",
    monthly_cost: true,
    additional_info: true,
  },
  [EXPENSES_TYPES.OTHER_DEBT]: {
    label: "Lån från andra bolag",
    description:
      "Ange totalt månadsbelopp för lån du har från andra bolag som inte syns i din kreditupplysning.",
    placeholder: "Återbetalning av andra lån (kr/mån)",
    monthly_cost: true,
    additional_info: true,
  },
  [EXPENSES_TYPES.PRIVATE_LEASING]: {
    label: "Privatleasing",
    description: "Ange totalt månadsbelopp enligt leasingavtal.",
    placeholder: "Månadsbelopp enligt leasingavtal (kr/mån)",
    monthly_cost: true,
    additional_info: true,
  },
  [EXPENSES_TYPES.GUARANTEE_COMMITMENT]: {
    label: "Borgensåtagande",
    description:
      "Ange det totala belopp som du har gått i borgen för någon annans lån.",
    placeholder: "Total belopp för borgensåtaganden (kr)",
    monthly_cost: false,
    additional_info: true,
  },
}

const expensesOptions = [
  {
    id: 0,
    value: "",
    name: "Välj",
  },
  {
    id: 1,
    value: EXPENSES_TYPES.CSN_LOAN,
    name: "Studielån från CSN",
  },
  {
    id: 2,
    value: EXPENSES_TYPES.PRIVATE_DEBT,
    name: "Lån från privatpersoner",
  },
  {
    id: 3,
    value: EXPENSES_TYPES.OTHER_DEBT,
    name: "Lån från andra bolag",
  },
  {
    id: 4,
    value: EXPENSES_TYPES.PRIVATE_LEASING,
    name: "Privatleasing",
  },
  {
    id: 5,
    value: EXPENSES_TYPES.GUARANTEE_COMMITMENT,
    name: "Borgensåtagande",
  },
]

type OtherExpense = {
  expense_type: ExpenseType | ""
  total_debt_amount: number
  monthly_cost: number
}

type FormValues = {
  employer: string
  loanPurpose: LoanPurposeType
  hasOtherExpenses: boolean
  otherExpenses: OtherExpense[]
}

const LoanPurpose: FC<StepPropsType> = ({ data }) => {
  const { id } = useParams() as { id: string }
  const { errorModal } = useFeedbackModal()
  const intl = useIntl()
  const loanPurposeOptions = useOptions()
  const updateLoanApplication = useUpdateLoanApplication()
  const [activeInfoModal, setActiveInfoModal] = useState<ExpenseType | null>(
    null
  )

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      employer: data.applicant.applicant_detail.employer || "",
      loanPurpose: data.loan_purpose || undefined,
      hasOtherExpenses:
        data.applicant.applicant_detail.has_other_expenses || false,
      otherExpenses: [],
    },
  })

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "otherExpenses",
  })

  const hasOtherExpenses = watch("hasOtherExpenses")

  const onSubmit = (formData: FormValues) => {
    updateLoanApplication.mutate(
      {
        payload: {
          applicant: {
            applicant_detail: {
              employer: formData.employer,
              has_other_expenses: formData.hasOtherExpenses,
              other_expenses: formData.hasOtherExpenses
                ? formData.otherExpenses
                : [],
            },
          },
          loan_purpose: formData.loanPurpose,
        },
        uid: id,
      },
      {
        onError: () => {
          errorModal()
        },
      }
    )
  }

  return (
    <>
      {activeInfoModal && (
        <ExpenseInfoModal
          expenseType={activeInfoModal}
          onClose={() => setActiveInfoModal(null)}
        />
      )}
      <Styles.Container>
        <h1>
          <FormattedMessage
            id="app.onboarding.loanPurpose.heading"
            defaultMessage="Nästan klart!"
          />
        </h1>
        <p>
          För att gå vidare med din ansökan behöver vi ytterliga information om
          dig.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "32px" }}
          >
            {!data.applicant.applicant_detail.employer && (
              <label htmlFor="employer" style={{ fontWeight: "bold" }}>
                Namnet på din arbetsgivare
                <Controller
                  name="employer"
                  control={control}
                  rules={{
                    required: "Det här fältet är obligatoriskt",
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Input
                      {...field}
                      placeholder={
                        <FormattedMessage
                          id="app.onboarding.loanPurpose.input.placeholder"
                          defaultMessage="Arbetsgivare"
                        />
                      }
                      type="text"
                      error={!!error}
                      message={error?.message}
                    />
                  )}
                />
              </label>
            )}

            {!data.loan_purpose && (
              <label htmlFor="loanPurpose" style={{ fontWeight: "bold" }}>
                Syfte till lånet
                <Controller
                  name="loanPurpose"
                  control={control}
                  rules={{ required: "Välj syfte för lånet" }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      {...field}
                      aria-label={intl.formatMessage({
                        id: "app.onboarding.loanPurpose.option1",
                        defaultMessage: "Välj syfte",
                      })}
                      options={loanPurposeOptions}
                      error={!!error}
                      message={error?.message}
                    />
                  )}
                />
              </label>
            )}

            {data.applicant.applicant_detail.has_other_expenses === null && (
              <div>
                <label
                  htmlFor="hasOtherExpenses"
                  style={{ fontWeight: "bold" }}
                >
                  Övriga lån och utgifter
                </label>
                <p>
                  Har du krediter ifrån andra långivare än banker och
                  kreditinstitut, såsom studielån, lån från privatpersoner,
                  leasingavtal eller borgensåtaganden?
                </p>

                <div role="radiogroup">
                  <Controller
                    name="hasOtherExpenses"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Radio
                          id="hasOtherExpenses-no"
                          value="false"
                          checked={!field.value}
                          onChange={() => {
                            field.onChange(false)
                            if (fields.length > 0) {
                              remove()
                            }
                          }}
                          labelText="Nej"
                        />

                        <Radio
                          id="hasOtherExpenses-yes"
                          value="true"
                          checked={field.value}
                          onChange={() => {
                            field.onChange(true)
                            if (fields.length === 0) {
                              append({
                                expense_type: "",
                                total_debt_amount: 0,
                                monthly_cost: 0,
                              })
                            }
                          }}
                          labelText="Ja"
                        />
                      </>
                    )}
                  />
                </div>
              </div>
            )}

            {data.applicant.applicant_detail.has_other_expenses === null &&
              hasOtherExpenses && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {fields.map((otherExpense, index) => (
                    <div
                      key={otherExpense.id}
                      style={{
                        backgroundColor: "var(--very-light-gray)",
                        borderRadius: "16px",
                        padding: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <Controller
                          name={`otherExpenses.${index}.expense_type`}
                          control={control}
                          rules={{ required: "Välj typ av utgift" }}
                          render={({ field, fieldState: { error } }) => (
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                }}
                              >
                                <span>
                                  Vilket typ av lån eller utgift är det?
                                </span>
                                <StyledCloseButton
                                  onClick={() => {
                                    remove(index)
                                    if (fields.length <= 1) {
                                      setValue("hasOtherExpenses", false)
                                    }
                                  }}
                                  type="button"
                                >
                                  Ta bort
                                </StyledCloseButton>
                              </div>
                              <Select
                                {...field}
                                options={expensesOptions}
                                error={!!error}
                                message={error?.message}
                                onChange={(e) => {
                                  field.onChange(e.target.value)
                                  update(index, {
                                    expense_type: e.target.value as ExpenseType, // TODO
                                    total_debt_amount: 0,
                                    monthly_cost: 0,
                                  })
                                }}
                              />
                            </div>
                          )}
                        />

                        {watch(`otherExpenses.${index}.expense_type`) && (
                          <Controller
                            name={`otherExpenses.${index}.${
                              expenses[
                                watch(`otherExpenses.${index}.expense_type`)
                              ].monthly_cost
                                ? "monthly_cost"
                                : "total_debt_amount"
                            }`}
                            control={control}
                            rules={{
                              required: "Ange ett belopp",
                              min: {
                                value: 1,
                                message: "Beloppet måste vara större än 0",
                              },
                              validate: (value) =>
                                Number.isInteger(value) || "Ange ett heltal",
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <div>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "400",
                                    color: "var(--dark-gray)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  {
                                    expenses[
                                      watch(
                                        `otherExpenses.${index}.expense_type`
                                      )
                                    ].description
                                  }
                                  {expenses[
                                    watch(`otherExpenses.${index}.expense_type`)
                                  ]?.additional_info && (
                                    <InfoIconButton
                                      type="button"
                                      onClick={() => {
                                        const expenseType = watch(
                                          `otherExpenses.${index}.expense_type`
                                        )
                                        if (expenseType) {
                                          setActiveInfoModal(expenseType)
                                        }
                                      }}
                                      aria-label="More information"
                                    >
                                      <IconFactory name="info24" />
                                    </InfoIconButton>
                                  )}
                                </span>
                                <Input
                                  {...field}
                                  type="number"
                                  placeholder={
                                    expenses[
                                      watch(
                                        `otherExpenses.${index}.expense_type`
                                      )
                                    ].placeholder
                                  }
                                  error={!!error}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  message={error?.message}
                                />
                              </div>
                            )}
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  <div style={{ display: "flex" }}>
                    <Button
                      type="button"
                      variant="inverse"
                      onClick={() =>
                        append({
                          expense_type: "",
                          total_debt_amount: 0,
                          monthly_cost: 0,
                        })
                      }
                      disabled={fields.length >= 10}
                    >
                      Lägg till lån eller utgift
                    </Button>
                  </div>
                </div>
              )}

            <div style={{ display: "flex" }}>
              <Button
                type="submit"
                disabled={
                  updateLoanApplication.isLoading ||
                  !watch("employer") ||
                  !watch("loanPurpose")
                }
                isLoading={updateLoanApplication.isLoading}
              >
                <FormattedMessage
                  id="app.onboarding.loanPurpose.nextCTAText"
                  defaultMessage="Gå vidare"
                />
              </Button>
            </div>
          </div>
        </form>

        <Styles.MobileAttentionBox>
          <LoanPerksBox estimatedCashBack={data.estimated_cashback} />
        </Styles.MobileAttentionBox>
      </Styles.Container>
    </>
  )
}

export default LoanPurpose
